import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Check,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  X,
} from 'lucide-react';
import { Dialog } from '../../components/Dialog.js';
import { useToast } from '../../hooks/useToast.js';
import { useWakeLock } from '../../hooks/useWakeLock.js';
import type { Recipe } from '../../api/types.js';
import { CountdownTimer } from './CountdownTimer.js';
import { Stepper } from './Stepper.js';
import {
  extractFirstDuration,
  formatAmount,
  parseSteps,
  scaleIngredientAmount,
} from './cookingModeUtils.js';

export interface CookingModeProps {
  open: boolean;
  recipe: Recipe | null;
  initialPortions: number;
  onClose: () => void;
}

const HEADER_TESTID = 'cooking-mode-header';
const STATUS_TESTID = 'cooking-wakelock';

function wakeLockLine(status: string): string {
  if (status === 'acquired') return '💡 Screen remains active (WakeLock)';
  if (status === 'acquiring') return '💡 Acquiring WakeLock…';
  if (status === 'unsupported') return '💡 Wake Lock unavailable on this device';
  return '💡 Wake Lock released';
}

export function CookingMode({
  open,
  recipe,
  initialPortions,
  onClose,
}: CookingModeProps): ReactElement | null {
  const toast = useToast();
  const wakeLock = useWakeLock(open);
  const [portions, setPortions] = useState<number>(initialPortions);
  const [stepIndex, setStepIndex] = useState<number>(0);

  const recipeId = recipe?.id ?? null;

  useEffect(() => {
    if (recipeId === null) return;
    setPortions(initialPortions);
    setStepIndex(0);
  }, [recipeId]);

  const steps = useMemo<string[]>(() => {
    return recipe ? parseSteps(recipe.instructions) : [];
  }, [recipe]);

  const scaledIngredients = useMemo(() => {
    if (!recipe) return [];
    const defaultPortions = recipe.portions > 0 ? recipe.portions : 1;
    return recipe.ingredients.map((line) => ({
      key: `${line.ingredient_id}-${line.unit}`,
      name: line.name,
      amount: formatAmount(
        scaleIngredientAmount(line.amount, portions, defaultPortions),
      ),
      unit: line.unit,
    }));
  }, [recipe, portions]);

  if (!recipe) return null;
  if (!open) return null;

  const clampedPortions = Math.max(1, Math.floor(portions));
  const safeStepIndex = steps.length === 0 ? 0 : Math.min(stepIndex, steps.length - 1);
  const currentStep = steps[safeStepIndex] ?? '';
  const isFinalStep = safeStepIndex >= steps.length - 1;
  const duration = extractFirstDuration(currentStep);

  function dec(): void {
    setPortions((p) => Math.max(1, Math.floor(p) - 1));
  }
  function inc(): void {
    setPortions((p) => Math.max(1, Math.floor(p) + 1));
  }
  function back(): void {
    setStepIndex((i) => Math.max(0, i - 1));
  }
  function next(): void {
    if (isFinalStep) {
      done();
      return;
    }
    setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  }
  function done(): void {
    toast.push('success', `Cooking complete: ${recipe?.title ?? ''}`);
    onClose();
  }
  function handleClose(): void {
    wakeLock.release();
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} title="COOKING MODE">
      <div className="flex max-h-[80vh] flex-col gap-4" data-testid="cooking-mode">
        <header
          className="sticky top-0 -mx-5 flex flex-col gap-1 border-b px-5 pb-3"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}
          data-testid={HEADER_TESTID}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span aria-hidden="true">👨‍🍳</span>
              <ChefHat size={18} aria-hidden="true" style={{ color: 'var(--accent)' }} />
              <span
                className="text-sm font-bold uppercase tracking-wide"
                style={{ color: 'var(--text-main)' }}
              >
                Cooking Mode
              </span>
            </div>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close cooking mode"
              className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md border"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--text-main)',
                background: 'transparent',
              }}
              data-testid="cooking-close"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <div
            className="text-base font-semibold"
            style={{ color: 'var(--text-main)' }}
            data-testid="cooking-recipe-title"
          >
            {recipe.title}
          </div>
          <div
            className="text-xs"
            style={{ color: 'var(--text-muted)' }}
            data-testid={STATUS_TESTID}
            data-status={wakeLock.status}
          >
            {wakeLockLine(wakeLock.status)}
          </div>
        </header>

        <div className="flex items-center justify-between gap-3" data-testid="cooking-portions">
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Portions
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Decrease portions"
              onClick={dec}
              disabled={clampedPortions <= 1}
              className="flex h-12 w-12 items-center justify-center rounded-md border"
              style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
              data-testid="cooking-portions-dec"
            >
              <Minus size={18} aria-hidden="true" />
            </button>
            <span
              className="min-w-[3ch] text-center text-base font-semibold"
              style={{ color: 'var(--text-main)' }}
              data-testid="cooking-portions-value"
            >
              {clampedPortions}
            </span>
            <button
              type="button"
              aria-label="Increase portions"
              onClick={inc}
              className="flex h-12 w-12 items-center justify-center rounded-md border"
              style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
              data-testid="cooking-portions-inc"
            >
              <Plus size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        <section data-testid="cooking-ingredients-section">
          <h3
            className="mb-2 text-sm font-semibold"
            style={{ color: 'var(--text-main)' }}
          >
            Ingredients for {clampedPortions} Portions:
          </h3>
          {scaledIngredients.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              No ingredients listed.
            </p>
          ) : (
            <ul
              className="flex flex-col gap-1 text-sm"
              style={{ color: 'var(--text-main)' }}
            >
              {scaledIngredients.map((row) => (
                <li
                  key={row.key}
                  data-testid="cooking-ingredient"
                  data-amount={row.amount}
                >
                  • {row.amount}
                  {row.unit} {row.name}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="cooking-steps-section">
          <h3
            className="mb-2 text-sm font-semibold"
            style={{ color: 'var(--text-main)' }}
          >
            Step {safeStepIndex + 1} of {steps.length}:
          </h3>
          {steps.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              No instructions provided.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <p
                className="text-sm"
                style={{ color: 'var(--text-main)' }}
                data-testid="cooking-current-step"
              >
                {currentStep}
              </p>
              <Stepper count={steps.length} activeIndex={safeStepIndex} />
              {duration ? (
                <CountdownTimer
                  key={`timer-${safeStepIndex}-${duration.totalSeconds}`}
                  durationSeconds={duration.totalSeconds}
                  label={duration.label}
                />
              ) : null}
            </div>
          )}
        </section>

        <footer
          className="flex items-center justify-between gap-3"
          data-testid="cooking-step-nav"
        >
          <button
            type="button"
            onClick={back}
            disabled={safeStepIndex === 0}
            aria-label="Previous step"
            className="flex min-h-[48px] items-center justify-center gap-1 rounded-md border px-3 text-sm font-medium disabled:opacity-50"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
              background: 'transparent',
            }}
            data-testid="cooking-back"
          >
            <ChevronLeft size={18} aria-hidden="true" />
            Back
          </button>
          {isFinalStep ? (
            <button
              type="button"
              onClick={done}
              className="flex min-h-[48px] items-center justify-center gap-1 rounded-md px-4 text-sm font-medium"
              style={{
                background: 'var(--accent)',
                color: 'var(--accent-contrast)',
              }}
              data-testid="cooking-done"
            >
              <Check size={18} aria-hidden="true" />
              Done
            </button>
          ) : (
            <button
              type="button"
              onClick={next}
              className="flex min-h-[48px] items-center justify-center gap-1 rounded-md px-3 text-sm font-medium"
              style={{
                borderColor: 'var(--accent)',
                color: 'var(--accent)',
                background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
              }}
              data-testid="cooking-next"
              aria-label="Next step"
            >
              Next
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          )}
        </footer>
      </div>
    </Dialog>
  );
}
